import {
  ApolloLink,
  type FetchResult,
  type NextLink,
  Observable,
  type Operation,
} from "@apollo/client";
import { print } from "graphql";

function isFile(value: unknown): value is Blob {
  return typeof Blob !== "undefined" && value instanceof Blob;
}

/**
 * Walk `variables`, recording every File/Blob and the dot-paths it appears at,
 * and return a copy with those positions nulled (the GraphQL multipart request
 * spec sends files out-of-band, referenced by `map`).
 */
export function extractFiles(variables: Record<string, unknown>): {
  clean: Record<string, unknown>;
  files: Map<Blob, string[]>;
} {
  const files = new Map<Blob, string[]>();
  const recurse = (value: unknown, path: string): unknown => {
    if (isFile(value)) {
      const paths = files.get(value) ?? [];
      paths.push(path);
      files.set(value, paths);
      return null;
    }
    if (Array.isArray(value)) return value.map((v, i) => recurse(v, `${path}.${i}`));
    if (value && typeof value === "object") {
      const out: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
        out[k] = recurse(v, `${path}.${k}`);
      }
      return out;
    }
    return value;
  };
  const clean = recurse(variables, "variables") as Record<string, unknown>;
  return { clean, files };
}

/**
 * Terminating-on-files Apollo link implementing the GraphQL multipart request
 * spec (https://github.com/jaydenseric/graphql-multipart-request-spec).
 *
 * Operations whose variables contain a File/Blob (e.g. `createPost`'s `media`)
 * are sent as `multipart/form-data`; everything else falls through to the next
 * (http) link unchanged. Hand-rolled to stay on Apollo Client v3 (the published
 * `apollo-upload-client` now targets v4 only) and to mirror the API's own
 * hand-rolled multipart parser.
 */
export class UploadLink extends ApolloLink {
  constructor(private readonly uri: string) {
    super();
  }

  override request(operation: Operation, forward?: NextLink): Observable<FetchResult> | null {
    const { clean, files } = extractFiles(operation.variables ?? {});
    if (files.size === 0) return forward ? forward(operation) : null;

    const form = new FormData();
    form.append(
      "operations",
      JSON.stringify({
        query: print(operation.query),
        variables: clean,
        operationName: operation.operationName ?? null,
      }),
    );

    const map: Record<string, string[]> = {};
    const ordered: Blob[] = [];
    let i = 0;
    for (const [file, paths] of files) {
      map[String(i)] = paths;
      ordered.push(file);
      i++;
    }
    form.append("map", JSON.stringify(map));
    ordered.forEach((file, idx) => {
      const name = file instanceof File ? file.name : `upload-${idx}`;
      form.append(String(idx), file, name);
    });

    // Forward context headers (e.g. the Bearer token set by authLink) but never
    // content-type — the browser sets the multipart boundary itself.
    const ctxHeaders = (operation.getContext().headers ?? {}) as Record<string, string>;
    const headers: Record<string, string> = {};
    for (const [k, v] of Object.entries(ctxHeaders)) {
      if (k.toLowerCase() !== "content-type") headers[k] = v;
    }

    return new Observable<FetchResult>((observer) => {
      const controller = new AbortController();
      fetch(this.uri, {
        method: "POST",
        credentials: "include",
        headers,
        body: form,
        signal: controller.signal,
      })
        .then((res) => res.json())
        .then((result) => {
          observer.next(result);
          observer.complete();
        })
        .catch((err) => observer.error(err));
      return () => controller.abort();
    });
  }
}
